import { useEffect, useState } from 'react';

import { APOLLO_CLIENTS, CustomContext } from '@/graphql/client';
import type { OrderAppCtaType } from '@/graphql/orderApp/api';
import { useProductVariantsBusinessDetailsLazyQuery } from '@/graphql/orderApp/api';
import type {
  StrapAddToBagProductsQuery,
  StrapAddToBagToolsQuery,
  StrapAttributesFragment,
} from '@/graphql/saleor/api';
import { useStrapAddToBagProductsQuery } from '@/graphql/saleor/api';
import { getMultiselectValue } from '@/lib/attributes';
import { NEXT_PUBLIC_IMAGES_FORMAT, THUMBNAIL_IMAGE_SIZE } from '@/lib/const';
import { mapEdgesToItems } from '@/lib/maps';
import { fetchTools } from '@/lib/product';
import { useRegionProvider } from '@/providers/RegionProvider';

export type StrapAddToBagBuckle = NonNullable<
  StrapAddToBagProductsQuery['productVariants']
>['edges'][number]['node'];

export type StrapAddToBagTool = NonNullable<
  NonNullable<
    StrapAddToBagToolsQuery['products']
  >['edges'][number]['node']['defaultVariant']
>;

/**
 *
 * @param compatibleBuckles
 * @param orderCtaType filter buckles by cta
 */
export const useBucklesAndTools = (
  compatibleBuckles: StrapAttributesFragment['compatibleBuckles'],
  orderCtaType?: OrderAppCtaType | null,
  lazy?: boolean,
) => {
  const [buckles, setBuckles] = useState<StrapAddToBagBuckle[] | null>(null);
  const [tools, setTools] = useState<StrapAddToBagTool[] | null>(null);
  const [loading, setLoading] = useState(false);

  const { query } = useRegionProvider();

  const { refetch: fetchBuckles } = useStrapAddToBagProductsQuery({
    variables: {
      limit: 50,
      thumbnailSize: THUMBNAIL_IMAGE_SIZE,
      imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
      ...query,
    },
    skip: true,
    fetchPolicy: 'cache-first',
  });

  const compatibleBucklesReferences = getMultiselectValue(compatibleBuckles);
  const [fetchBusinessData] = useProductVariantsBusinessDetailsLazyQuery({
    variables: {
      skus: compatibleBucklesReferences,
      channel: query.channel,
    },
    context: CustomContext({
      client: APOLLO_CLIENTS.orderApp,
      disableErrorNotification: true,
    }),
    fetchPolicy: 'network-only',
  });

  const update = async (
    initialCompatibleBuckles?: StrapAttributesFragment['compatibleBuckles'],
  ) => {
    const compatibleBucklesReferences = getMultiselectValue(
      initialCompatibleBuckles ?? compatibleBuckles,
    );
    setLoading(true);

    if (compatibleBucklesReferences.length) {
      const { data: productBucklesData } = await fetchBuckles({
        filter: { sku: compatibleBucklesReferences },
      });
      let buckleProducts = mapEdgesToItems(productBucklesData.productVariants);
      if (orderCtaType) {
        const { data: orderAppData } = await fetchBusinessData({
          variables: {
            skus: compatibleBucklesReferences,
            channel: query.channel,
          },
        });
        buckleProducts = buckleProducts.filter((item) => {
          const cta = orderAppData?.productVariants?.find(
            (jtem) => jtem.sku === item.sku,
          );
          return cta?.variantCTA === orderCtaType;
        });
      }

      buckleProducts.length && setBuckles(buckleProducts);
    }

    const tools = await fetchTools(query);
    tools.length && setTools(tools);

    setLoading(false);
    return { buckles, tools };
  };

  useEffect(() => {
    if (lazy) return;
    update();
  }, [compatibleBuckles, lazy]);

  return { buckles, tools, loading, update };
};
